import {
  Button,
  FormField,
  FormGrid,
  PresetRange,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { conversationsApi } from 'apis/conversations';
import {
  HookFormDateRangePicker,
  HookFormDateRangePickerValue,
  HookFormText,
} from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useCurrentUser } from 'utils/user/useCurrentUser';

type FieldValues = {
  email: string;
  dateRange: HookFormDateRangePickerValue;
};

export const ExportConversationsForm = (): JSX.Element => {
  const notify = useNotifications();
  const {
    user: { email },
  } = useCurrentUser();

  const [createConversationsCsv] =
    conversationsApi.useCreateConversationsCsvMutation();

  const form = useForm<FieldValues>({
    mode: 'all',
    defaultValues: {
      email: email ?? '',
    },
  });

  const dateRange = useWatch({ control: form.control, name: 'dateRange' });

  const numberOfDays = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return 0;
    return moment(dateRange.endDate).diff(dateRange.startDate, 'days');
  }, [dateRange]);

  const presets: PresetRange[] = useMemo(() => {
    const todayStart = moment().startOf('day');
    const todayEnd = moment().endOf('day');

    return [
      {
        key: 'last_7_days',
        label: 'Last 7 days',
        startDate: todayStart.clone().subtract(7, 'days'),
        endDate: todayEnd,
      },
      {
        key: 'last_14_days',
        label: 'Last 14 days',
        startDate: todayStart.clone().subtract(14, 'days'),
        endDate: todayEnd,
      },
      {
        key: 'last_30_days',
        label: 'Last 30 days',
        startDate: todayStart.clone().subtract(30, 'days'),
        endDate: todayEnd,
      },
      {
        key: 'last_90_days',
        label: 'Last 90 days',
        startDate: todayStart.clone().subtract(90, 'days'),
        endDate: todayEnd,
      },
      {
        key: 'last_month',
        label: 'Last month',
        startDate: todayStart.clone().subtract(1, 'month').set('date', 1),
        endDate: todayEnd.clone().set('date', 0),
      },
      {
        key: 'this_month',
        label: 'This month',
        startDate: todayStart.clone().set('date', 1),
        endDate: todayEnd,
      },
    ];
  }, []);

  const handleSubmit: SubmitHandler<FieldValues> = async (
    values,
  ): Promise<void> => {
    try {
      const { startDate, endDate } = values.dateRange;

      await createConversationsCsv({
        email: values.email,
        dateRange: [
          moment(startDate).format('YYYY-MM-DD'),
          moment(endDate).format('YYYY-MM-DD'),
        ],
      }).unwrap();

      notify(
        {
          title: 'Conversation export in progress',
          text: `We will send a link to the CSV file to ${values.email}.\n\nIt should only take a few minutes, but in some cases it can take much longer depending on how much data you are exporting.`,
          icon: 'i-done',
          time: 10000,
          showDesktop: false,
        },
        NotificationType.Success,
      );

      ampli.analyticsExportMyDataButtonPressed({
        numberOfDays,
      });
    } catch (error) {
      notify(
        {
          title: 'Error while exporting the conversations.',
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <FormGrid>
      <FormField span={3}>
        <HookFormText
          name="email"
          label="Send to email"
          control={form.control}
          rules={{
            required: 'Email is required',
          }}
          componentProps={{
            autoFocus: true,
            type: 'email',
          }}
        />
      </FormField>

      <FormField span={3}>
        <HookFormDateRangePicker
          name="dateRange"
          label="Date range"
          control={form.control}
          rules={{
            required: 'Date range is required',
            validate: {
              tooLongRange: () =>
                numberOfDays <= 90 ||
                'Choose a date range no longer than 90 days',
            },
          }}
          componentProps={{
            placeholder: 'Select a date range',
            dateRangePickerProps: {
              presets,
              inputsPlacement: 'top',
              withDateInput: true,
            },
            maxDate: moment().add(1, 'day'),
          }}
        />
      </FormField>

      <FormField span={3}>
        <Button
          variant="primary"
          disabled={!form.formState.isValid || form.formState.isSubmitting}
          onClick={form.handleSubmit(handleSubmit)}
        >
          Start export
        </Button>
      </FormField>
    </FormGrid>
  );
};
